/*
 *  Copyright © 2025 Cenozon Inc. All Rights Reserved.
 */
import axios from 'axios';
import { getAccessToken } from '@cenozon/indoraptor-user-auth-react-spa';
import serviceCall from '../serviceCall';
import { userManageReducerConstant, userReducerConstant } from '../reducerConstant';
import { sessionStorageKey, displayText, statusCode, apiRouter, stringConstant } from '../../constant';
import { showSuccessSnackbar, showFailureSnackbar, showWarningSnackbar } from './snackbarAction';
import _ from 'lodash';
import {
   decryptData, isStatusCodeValid, encryptData, isEmpty, clearStorageItems
} from '../../components/shared/helper';
import { arrayConstants } from '../../arrayconstants';

const PORTAL = (process.env.REACT_APP_INDORAPTOR_PORTAL_URL ||
   'https://portal.cenozon.com/').replace(/\/?$/, '/');

export const setAuthError = payload =>
   ({ type: userReducerConstant.SET_AUTH_ERROR, value: payload });

export const clearAuthError = () =>
   ({ type: userReducerConstant.CLEAR_AUTH_ERROR });

// Hydrates USER_DETAILS / USER_CURRENT_CLIENT_DETAILS from /api/users/me.
// Uses axios directly (not serviceCall) so that the response body on 401/403
// is preserved and we can route the user to the AccessDenied screen with the
// server's `{ error, message }` payload intact.
export const fetchCurrentUser = () => dispatch => {
   dispatch(toggleLoader(true));
   const apiUrl = process.env.REACT_APP_API_URL ?? stringConstant.EMPTY_STRING;
   const token = getAccessToken();
   return axios
      .get(`${apiUrl}${displayText.API}/${apiRouter.USERS}/me`, {
         headers: {
            'Content-Type': displayText.APPLICATION_JSON,
            ...(token ? { Authorization: `${displayText.BEARER} ${token}` } : {})
         }
      })
      .then(result => {
         const me = result.data;
         encryptData(me, sessionStorageKey.USER_DETAILS);
         const initialClient = me?.clients?.[arrayConstants.initialOrder];
         if (initialClient) {
            encryptData(initialClient, sessionStorageKey.USER_CURRENT_CLIENT_DETAILS);
            dispatch(userClientInfo(initialClient));
         }
         dispatch(userLoginApi(result));
         dispatch(getUserFeatures(apiRouter.FEATURES));
         const userGuid = me?.usersGuid;
         const clientsGuid = initialClient?.clientsGuid;
         if (userGuid && clientsGuid) {
            const prefUrl = `${userReducerConstant.COMMON_USER_PREFERENCE}?${displayText.USER_GUID}=${userGuid}&${displayText.CLIENTSGUID}=${clientsGuid}`;
            return serviceCall.getAllData(prefUrl).then(userPreferenceResult => {
               if (isStatusCodeValid(userPreferenceResult, statusCode.CODE_200)) {
                  const metricPreferenceResult = getMetricPreference(
                     userPreferenceResult,
                     displayText.METRIC_PREF
                  );
                  dispatch(SetUpdateImperialPreference(metricPreferenceResult.isImperial));
                  setPrefToSession(userPreferenceResult, displayText.METRIC_PREF, sessionStorageKey.METRIC_PREF);
                  setPrefToSession(userPreferenceResult, displayText.USER_TABLE, sessionStorageKey.USER_PREF);
                  setPrefToSession(userPreferenceResult, displayText.CLIENT_TABLE, sessionStorageKey.CLIENT_PREF);
                  setPrefToSession(userPreferenceResult, displayText.ADMIN_TABLE, sessionStorageKey.ADMIN_PREF);
               } else {
                  dispatch(showFailureSnackbar(userPreferenceResult));
               }
               dispatch(toggleLoader(false));
            });
         }
         dispatch(toggleLoader(false));
      })
      .catch(err => {
         dispatch(toggleLoader(false));
         const status = err?.response?.status;
         const body = err?.response?.data;
         if (status === statusCode.CODE_401 || status === 403) {
            // Authenticated at the portal but no provisioned ILI account
            // (or token rejected). Surface the server's message on the
            // AccessDenied screen — do NOT snackbar, do NOT loop.
            dispatch(setAuthError({
               status,
               code: body?.error ?? 'unauthorized',
               message: body?.message
            }));
            return;
         }
         dispatch(showFailureSnackbar(err?.message ?? err));
      });
};

// Indoraptor SSO logout: end the Keycloak session via the realm's OIDC
// logout endpoint, then bounce back to the SPA root. `client_id` is required
// by Keycloak when no id_token_hint is supplied.
export const logout = () => () => {
   clearStorageItems();
   const redirect = encodeURIComponent(`${window.location.origin}/`);
   const logoutUrl = `${PORTAL}auth/realms/cenozon/protocol/openid-connect/logout`
      + `?client_id=indoraptor&post_logout_redirect_uri=${redirect}`;
   window.location.href = logoutUrl;
};

export const setUserClientDetails = val => {
   return dispatch => {
      dispatch(userClientInfo(val));
   };
};

const userClientInfo = val => ({ type: userReducerConstant.USER_CLIENT_INFO, value: val });
const userLoginApi = val => ({ type: userReducerConstant.LOGIN_USER, value: val });
const toggleLoader = val => ({ type: userReducerConstant.TOGGLE_LOADER, value: val });
export const setErrMsg = val => ({ type: userReducerConstant.SET_ERR_MSG, value: val });

export const getUserFeatures = url => {
   return dispatch => {
      dispatch(startFeaturesLoader(true));
      return serviceCall
         .getAllData(url)
         .then(result => {
            if (!(result && result.data)) {
               dispatch(startFeaturesLoader(true));
               dispatch(showFailureSnackbar(result));
               return;
            }
            encryptData(result.data, sessionStorageKey.USER_ROLES);
            dispatch(fetchUserMenu(result));
         })
         .catch(e => {
            dispatch(startFeaturesLoader(true));
            dispatch(showFailureSnackbar(e));
         });
   };
};

export const getPreference = url => {
   return dispatch => {
      return serviceCall
         .getAllData(url)
         .then(result => {
            if (isStatusCodeValid(result, statusCode.CODE_200)) {
               const getMetricPreferenceData = getMetricPreference(
                  result, displayText.METRIC_PREF
               );
               dispatch(SetUpdateImperialPreference(getMetricPreferenceData.isImperial));
               setPrefToSession(result, displayText.METRIC_PREF, sessionStorageKey.METRIC_PREF);
               setPrefToSession(result, displayText.USER_TABLE, sessionStorageKey.USER_PREF);
               setPrefToSession(result, displayText.CLIENT_TABLE, sessionStorageKey.CLIENT_PREF);
               setPrefToSession(result, displayText.ADMIN_TABLE, sessionStorageKey.ADMIN_PREF);
            } else {
               dispatch(showFailureSnackbar(result));
            }
         })
         .catch(e => {
            dispatch(showFailureSnackbar(e));
         });
   };
};

const fetchUserMenu = val => {
   return { type: userReducerConstant.FETCH_USER_MENU, value: val };
};

const startFeaturesLoader = (val) => {
   return { type: userReducerConstant.SET_FEATURES_LOADER, value: val };
};

export const getMyProfile = url => {
   return dispatch => {
      dispatch(startLoader());
      dispatch(setUpdateMyProfileSuccess(null));
      return serviceCall
         .getAllData(url)
         .then(result => {
            result && result.data
               ? dispatch(fetchMyprofileSuccess(result))
               : dispatch(showFailureSnackbar(result));
         })
         .catch(e => {
            dispatch(showFailureSnackbar(e));
         });
   };
};

const fetchMyprofileSuccess = val => {
   return { type: userReducerConstant.FETCH_MY_PROFILE, value: val };
};

const startLoader = () => {
   return { type: userReducerConstant.MY_PROFILE_LOADER };
};

export const updateUsername = sessiondata => {
   return dispatch => {
      dispatch({ type: userReducerConstant.UPDATE_USERNAME, value: sessiondata });
   };
};

export const updateMyProfile = (url, data) => {
   return dispatch => {
      dispatch(setPasswordLoaderShow(true));
      return serviceCall
         .editData(url, data)
         .then(result => {
            if (isStatusCodeValid(result, statusCode.CODE_200)) {
               dispatch(setUpdateMyProfileSuccess(null));
               dispatch(showSuccessSnackbar(displayText.SUCCESS));
            } else {
               dispatch(showFailureSnackbar(result));
            }
         })
         .catch(e => {
            dispatch(showFailureSnackbar(e));
         });
   };
};

export const setUpdateMyProfile = val => {
   return dispatch => {
      dispatch(setUpdateMyProfileSuccess(val));
   };
};

export const SetUpdateImperialPreference = (val) => {
   return (dispatch) => {
      dispatch({ type: userManageReducerConstant.UPDATE_IMPERIAL_PREFERENCE, value: val });
   };
};

export const setUpdateMyProfileSuccess = (val) => {
   return { type: userReducerConstant.SET_UPDATE_MY_PROFILE, value: val };
};

export const updateImperialPreference = (url, data, userId, clientId, selection) => {
   return (dispatch) => {
      return serviceCall
         .postData(url, data)
         .then(result => {
            if (isStatusCodeValid(result, statusCode.CODE_200)) {
               dispatch(SetUpdateImperialPreference(selection));
               const prefUrl = `${apiRouter.COMMON}/${apiRouter.COMMON_USER_PREFERENCE}?${displayText.USER_GUID}=${userId}&${displayText.CLIENTSGUID}=${clientId}`;
               dispatch(getPreference(prefUrl));
               return dispatch(showSuccessSnackbar(displayText.SUCCESS));
            }
            dispatch(showFailureSnackbar(result));
         })
         .catch((e) => {
            dispatch(showFailureSnackbar(e));
         });
   };
};

// Change the current user's portal password. Delegates to the WebAPI's
// /api/users/password endpoint, which proxies to the Indoraptor portal's
// IUserAccountService.SetPassword. Returns the server's response so the
// caller can branch on status (e.g. surface the message on 401 / 400).
export const changePassword = (currentPassword, newPassword) => {
   return dispatch => {
      dispatch(setPasswordLoaderShow(true));
      dispatch(validationMessage(stringConstant.EMPTY_STRING));
      return serviceCall
         .postData(`${apiRouter.USERS}/password`, { currentPassword, newPassword })
         .then(result => {
            if (isStatusCodeValid(result, statusCode.CODE_200)) {
               dispatch(updatePasswordSuccess(true));
               dispatch(showSuccessSnackbar(displayText.SUCCESS));
               dispatch(setPasswordLoaderHide(false));
               return result;
            }
            const message = result?.data?.message ?? result;
            dispatch(validationMessage(message));
            dispatch(updatePasswordFailure(false));
            dispatch(setPasswordLoaderHide(false));
            return result;
         })
         .catch(err => {
            dispatch(validationMessage(err?.message ?? err));
            dispatch(updatePasswordFailure(false));
            dispatch(setPasswordLoaderHide(false));
         });
   };
};

export const clearValidationMessage = () => {
   return dispatch => {
      dispatch(validationMessage(stringConstant.EMPTY_STRING));
   };
};

const setPasswordLoaderShow = val => {
   return { type: userReducerConstant.SET_PASSWORD_LODER_SHOW, value: val };
};

const setPasswordLoaderHide = val => {
   return { type: userReducerConstant.SET_PASSWORD_LODER_HIDE, value: val };
};

const updatePasswordSuccess = val => {
   return { type: userReducerConstant.UPDATE_PASSWORD_SUCCESS, value: val };
};

const updatePasswordFailure = val => {
   return { type: userReducerConstant.UPDATE_PASSWORD_FAILURE, value: val };
};

const validationMessage = val => {
   return { type: userReducerConstant.VALIDATION_MESSAGE, value: val };
};

function setPrefToSession(result, tableName, key) {
   const clientPrefFilter = _.filter(result.data, pref =>
      pref.entityName === tableName
   );
   const obj = {};
   if (clientPrefFilter.length > arrayConstants.nonEmptyArray) {
      _.map(clientPrefFilter, pref => {
         obj[pref.entityProperty] = pref.entityPreference;
      });
   }
   encryptData(obj, key);
}

function getMetricPreference(result, tableName) {
   const clientPrefFilter = _.filter(result.data, (pref) => {
      return pref.entityName === tableName;
   });
   const obj = {};
   if (clientPrefFilter.length > arrayConstants.nonEmptyArray) {
      _.map(clientPrefFilter, (pref) => {
         obj[pref.entityProperty] = pref.entityPreference;
      });
   }
   return obj;
}

export const selectedClientLoaderShow = val => {
   return dispatch => {
      dispatch(selectedClientLoader(val));
   };
};

const selectedClientLoader = val => {
   return { type: userReducerConstant.CLIENT_SELECT_LOADER, value: val };
};

export const setLogoutLoader = val => {
   return { type: userReducerConstant.SET_LOGOUT_LOADER, value: val };
};

export const removeLogoutLoader = val => {
   return { type: userReducerConstant.REMOVE_LOGOUT_LOADER, value: val };
};
